import { useEffect, useRef, useState, useMemo, type Dispatch, type SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, CalendarRange, Plus, X, ExternalLink, Pencil, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { useAuth } from '../auth/AuthProvider';
import { AccountAvatar } from '../components/AccountAvatar';
import { ReusePostButton } from '../components/ReusePostButton';
import {
  detectTimezone, timezoneLabel,
  toZonedDate, nowZoned, zonedToUtc, zonedDateToInput,
} from '../lib/timezones';

/* ── Types ──────────────────────────────────────────────── */
type PostStatus =
  | 'draft' | 'scheduled' | 'publishing' | 'published'
  | 'partial_failed' | 'failed' | 'canceled' | 'pending_approval';

type CalendarPost = {
  id: string;
  caption: string;
  media: { url: string; type?: string; thumbnailUrl?: string | null }[];
  status: PostStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  targets: {
    socialAccountId: string;
    status: string;
    kind: string;
    platform: string;
    platformUrl: string | null;
    username: string | null;
    displayName: string | null;
    profilePhotoUrl: string | null;
  }[];
};

/** A post plus its scheduled instant expressed as a zoned Date
    (UTC fields hold the wall-clock time in the user's timezone). */
type ZonedPost = CalendarPost & { zoned: Date };

/* ── Status styles ──────────────────────────────────────── */
const STATUS_STYLES: Record<PostStatus, { chip: string; dot: string; label: string }> = {
  published:      { chip: 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200', dot: 'bg-emerald-500', label: 'Publicada' },
  scheduled:      { chip: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200',         dot: 'bg-amber-500',   label: 'Programada' },
  failed:         { chip: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200',                 dot: 'bg-red-500',     label: 'Falló' },
  partial_failed: { chip: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200',     dot: 'bg-orange-500',  label: 'Parcial' },
  publishing:     { chip: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200',             dot: 'bg-blue-500',    label: 'Publicando' },
  draft:          { chip: 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200',         dot: 'bg-slate-400',   label: 'Borrador' },
  canceled:       { chip: 'bg-slate-100 text-slate-400 border-slate-200',                            dot: 'bg-slate-300',   label: 'Cancelada' },
  pending_approval: { chip: 'bg-violet-100 text-violet-800 border-violet-300 hover:bg-violet-200',   dot: 'bg-violet-500',  label: 'En aprobación' },
};

const LEGEND_PLATFORMS: Platform[] = [
  'instagram', 'facebook', 'twitter', 'linkedin', 'tiktok',
  'youtube', 'pinterest', 'threads', 'bluesky',
];

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const HOUR_ROW = 56; // px

/* ── Date helpers (operate on zoned Dates via UTC fields) ── */
const startOfDay = (d: Date) => { const x = new Date(d); x.setUTCHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; };
const sameDay = (a: Date, b: Date) =>
  a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
const startOfWeek = (d: Date) => {
  const x = startOfDay(d);
  const day = x.getUTCDay(); // 0=Sun
  return addDays(x, day === 0 ? -6 : 1 - day);
};
const startOfMonth = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const fmtTime = (d: Date) =>
  `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ── Main page ──────────────────────────────────────────── */
export function Calendar() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tz = user?.timezone ?? detectTimezone();

  const [view, setView]       = useState<'month' | 'week'>('month');
  const [cursor, setCursor]   = useState<Date>(() => startOfDay(nowZoned(tz)));
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<ZonedPost | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'error' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, kind: 'ok' | 'error' = 'ok') {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api<{ posts: CalendarPost[] }>('/api/posts'),
  });

  // posts with their scheduled instant converted to the user's timezone
  const posts = useMemo<ZonedPost[]>(
    () =>
      (data?.posts ?? [])
        .filter((p) => p.scheduledAt)
        .map((p) => ({ ...p, zoned: toZonedDate(new Date(p.scheduledAt!), tz) })),
    [data, tz],
  );

  const today = nowZoned(tz);

  const reschedule = useMutation({
    mutationFn: (v: { id: string; scheduledAt: string; successMsg: string }) =>
      api(`/api/posts/${v.id}/schedule`, {
        method: 'PATCH',
        body: JSON.stringify({ scheduledAt: v.scheduledAt }),
      }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['posts'] });
      const prev = qc.getQueryData<{ posts: CalendarPost[] }>(['posts']);
      if (prev) {
        qc.setQueryData(['posts'], {
          posts: prev.posts.map((p) => (p.id === v.id ? { ...p, scheduledAt: v.scheduledAt } : p)),
        });
      }
      return { prev };
    },
    onSuccess: (_d, v) => showToast(v.successMsg, 'ok'),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['posts'], ctx.prev);
      showToast('No se pudo cambiar la fecha. Inténtalo de nuevo.', 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });

  /* navigation */
  const goPrev  = () => setCursor((c) => (view === 'month'
    ? new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() - 1, 1))
    : addDays(c, -7)));
  const goNext  = () => setCursor((c) => (view === 'month'
    ? new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + 1, 1))
    : addDays(c, 7)));
  const goToday = () => setCursor(startOfDay(nowZoned(tz)));

  /* drop handlers — reschedule keeping the post's wall-clock time/minutes */
  function dropOnDay(postId: string, day: Date) {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    const utc = zonedToUtc(
      day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(),
      post.zoned.getUTCHours(), post.zoned.getUTCMinutes(), tz,
    );
    const dayLabel = cap(day.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }));
    reschedule.mutate({ id: postId, scheduledAt: utc.toISOString(), successMsg: `Publicación movida al ${dayLabel}` });
  }
  function dropOnSlot(postId: string, day: Date, hour: number) {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    const utc = zonedToUtc(
      day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(),
      hour, post.zoned.getUTCMinutes(), tz,
    );
    const mm = String(post.zoned.getUTCMinutes()).padStart(2, '0');
    const dayLabel = cap(day.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }));
    reschedule.mutate({ id: postId, scheduledAt: utc.toISOString(), successMsg: `Publicación movida al ${dayLabel} a las ${String(hour).padStart(2, '0')}:${mm}` });
  }

  /* schedule-new handlers → redirect to PostCreate with preset date/time */
  function scheduleOnDay(day: Date) {
    const at = zonedDateToInput(new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), 9, 0)));
    navigate(`/dashboard/posts/new?at=${encodeURIComponent(at)}`);
  }
  function scheduleOnSlot(day: Date, hour: number) {
    const at = zonedDateToInput(new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hour, 0)));
    navigate(`/dashboard/posts/new?at=${encodeURIComponent(at)}`);
  }

  /* title */
  const title = useMemo(() => {
    if (view === 'month') {
      return cap(cursor.toLocaleDateString('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' }));
    }
    const ws = startOfWeek(cursor);
    const we = addDays(ws, 6);
    const sameMonth = ws.getUTCMonth() === we.getUTCMonth();
    const left  = ws.toLocaleDateString('es-ES', { day: 'numeric', timeZone: 'UTC', ...(sameMonth ? {} : { month: 'short' }) });
    const right = we.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    return `${left} – ${right}`;
  }, [view, cursor]);

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarDays size={22} className="text-brand-600" /> Calendario</h1>
          <p className="mt-1 text-sm text-slate-500">
            Tus publicaciones programadas · arrastra para reprogramar
          </p>
          <p className="mt-0.5 text-xs text-slate-400">Zona horaria: {timezoneLabel(tz)}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* view toggle — month/week grids are desktop-only; mobile uses the
              agenda list, so we hide the toggle below md. */}
          <div className="hidden md:flex rounded-lg bg-slate-100 p-0.5">
            <button
              onClick={() => setView('month')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition',
                view === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            >
              <CalendarDays size={15} /> Mes
            </button>
            <button
              onClick={() => setView('week')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition',
                view === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            >
              <CalendarRange size={15} /> Semana
            </button>
          </div>
        </div>
      </div>

      {/* toolbar */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
            <ChevronLeft size={16} />
          </button>
          <button onClick={goNext} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
            <ChevronRight size={16} />
          </button>
          <button onClick={goToday} className="h-8 px-3 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
            Hoy
          </button>
        </div>
      </div>

      {/* calendar body */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
          <Loader2 size={16} className="animate-spin" /> Cargando publicaciones…
        </div>
      ) : (
        <>
          {/* Desktop: drag-and-drop month / week grids */}
          <div className="hidden md:block">
            {view === 'month' ? (
              <MonthView
                cursor={cursor}
                today={today}
                posts={posts}
                dragKey={dragKey}
                setDragKey={setDragKey}
                onDropDay={dropOnDay}
                onSchedule={scheduleOnDay}
                onSelect={setSelected}
              />
            ) : (
              <WeekView
                cursor={cursor}
                today={today}
                posts={posts}
                dragKey={dragKey}
                setDragKey={setDragKey}
                onDropSlot={dropOnSlot}
                onSchedule={scheduleOnSlot}
                onSelect={setSelected}
              />
            )}
          </div>
          {/* Mobile: a clean chronological agenda for the current month */}
          <div className="md:hidden">
            <AgendaView
              cursor={cursor}
              today={today}
              posts={posts}
              onSelect={setSelected}
              onSchedule={scheduleOnDay}
            />
          </div>
        </>
      )}

      {/* legend — drag hint is desktop-only, but the status colors help on
          both, so we keep it visible everywhere. */}
      <Legend hasPosts={posts.length > 0} />

      {/* preview modal */}
      {selected && (
        <PostPreviewModal
          post={selected}
          tz={tz}
          onClose={() => setSelected(null)}
        />
      )}

      {/* reschedule toast */}
      {toast && createPortal(
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] pointer-events-none animate-fade-in-up">
          <div className={cn(
            'flex items-center gap-2 rounded-xl px-4 py-2.5 shadow-lg text-sm font-medium text-white',
            toast.kind === 'ok' ? 'bg-slate-900' : 'bg-red-600',
          )}>
            {toast.kind === 'ok'
              ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
              : <AlertTriangle size={16} className="shrink-0" />}
            {toast.msg}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ── Agenda view (mobile) ───────────────────────────────── */
function AgendaView({
  cursor,
  today,
  posts,
  onSelect,
  onSchedule,
}: {
  cursor: Date;
  today: Date;
  posts: ZonedPost[];
  onSelect: (post: ZonedPost) => void;
  onSchedule: (day: Date) => void;
}) {
  // Posts whose zoned date falls in the cursor's month, grouped by day and
  // sorted chronologically (day asc, then time asc within the day).
  const groups = useMemo(() => {
    const y = cursor.getUTCFullYear();
    const m = cursor.getUTCMonth();
    const inMonth = posts
      .filter((p) => p.zoned.getUTCFullYear() === y && p.zoned.getUTCMonth() === m)
      .sort((a, b) => a.zoned.getTime() - b.zoned.getTime());

    const byDay = new Map<number, ZonedPost[]>();
    for (const p of inMonth) {
      const d = p.zoned.getUTCDate();
      const list = byDay.get(d) ?? [];
      list.push(p);
      byDay.set(d, list);
    }
    return [...byDay.entries()].map(([day, items]) => ({
      day,
      date: items[0]!.zoned,
      items,
    }));
  }, [cursor, posts]);

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-14 px-6 flex flex-col items-center gap-3 text-center">
        <CalendarDays size={32} className="text-slate-300" strokeWidth={1.5} />
        <p className="text-sm text-slate-500">No hay publicaciones este mes.</p>
        <button
          onClick={() => onSchedule(startOfDay(cursor))}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-lg px-3 py-2 transition"
        >
          <Plus size={15} /> Programar publicación
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(({ day, date, items }) => {
        const isToday = sameDay(date, today);
        const weekday = cap(date.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'UTC' }));
        const monthLabel = date.toLocaleDateString('es-ES', { month: 'short', timeZone: 'UTC' });
        return (
          <div key={day}>
            {/* Day header */}
            <div className="flex items-center gap-2.5 mb-2 px-1">
              <span
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-xl text-sm font-bold leading-none',
                  isToday ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700',
                )}
              >
                {day}
              </span>
              <div className="min-w-0">
                <p className={cn('text-sm font-semibold', isToday ? 'text-brand-700' : 'text-slate-700')}>
                  {weekday}
                </p>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">{monthLabel}</p>
              </div>
            </div>

            {/* Posts for that day */}
            <div className="space-y-2">
              {items.map((post) => {
                const st = STATUS_STYLES[post.status] ?? STATUS_STYLES.draft;
                return (
                  <button
                    key={post.id}
                    onClick={() => onSelect(post)}
                    className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 hover:border-slate-300 active:bg-slate-50 transition"
                  >
                    {/* media thumb or status bar */}
                    {post.media[0] ? (
                      <img
                        src={post.media[0].thumbnailUrl ?? post.media[0].url}
                        alt=""
                        loading="lazy"
                        className="h-12 w-12 rounded-lg object-cover bg-slate-100 shrink-0"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                        }}
                      />
                    ) : (
                      <span className={cn('h-12 w-1.5 rounded-full shrink-0', st.dot)} />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold tabular-nums text-slate-800">{fmtTime(post.zoned)}</span>
                        <span className={cn('inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border', st.chip)}>
                          {st.label}
                        </span>
                      </div>
                      <p className={cn('mt-0.5 text-sm truncate', post.caption ? 'text-slate-700' : 'text-slate-400 italic')}>
                        {post.caption || 'Sin texto'}
                      </p>
                      <div className="mt-1.5 flex -space-x-1.5">
                        {post.targets.slice(0, 5).map((t) => (
                          <ChipAccountAvatar key={t.socialAccountId} target={t} />
                        ))}
                        {post.targets.length > 5 && (
                          <span className="ml-2.5 text-[10px] font-semibold text-slate-400 self-center">
                            +{post.targets.length - 5}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Month view ─────────────────────────────────────────── */
function MonthView({
  cursor, today, posts, dragKey, setDragKey, onDropDay, onSchedule, onSelect,
}: {
  cursor: Date;
  today: Date;
  posts: ZonedPost[];
  dragKey: string | null;
  setDragKey: Dispatch<SetStateAction<string | null>>;
  onDropDay: (postId: string, day: Date) => void;
  onSchedule: (day: Date) => void;
  onSelect: (post: ZonedPost) => void;
}) {
  const gridStart = startOfWeek(startOfMonth(cursor));
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const month = cursor.getUTCMonth();
  // Which day (by ISO key) currently has its overflow popover open.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  // Close on ESC.
  useEffect(() => {
    if (!expandedKey) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpandedKey(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedKey]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* weekday header */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* day cells */}
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const inMonth = day.getUTCMonth() === month;
          const isToday = sameDay(day, today);
          const dayPosts = posts
            .filter((p) => sameDay(p.zoned, day))
            .sort((a, b) => a.zoned.getTime() - b.zoned.getTime());
          const key = day.toISOString();

          return (
            <div
              key={key}
              onDragOver={(e) => { e.preventDefault(); setDragKey(key); }}
              onDragLeave={() => setDragKey((k) => (k === key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                setDragKey(null);
                if (id) onDropDay(id, day);
              }}
              className={cn(
                'group min-h-[118px] border-b border-r border-slate-100 p-1.5 flex flex-col gap-1 transition-colors',
                (i + 1) % 7 === 0 && 'border-r-0',
                !inMonth && 'bg-slate-50/60',
                dragKey === key && 'bg-brand-50 ring-1 ring-inset ring-brand-300',
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn(
                  'inline-flex items-center justify-center h-6 w-6 text-xs font-semibold rounded-full',
                  isToday ? 'bg-brand-600 text-white' : inMonth ? 'text-slate-600' : 'text-slate-300',
                )}>
                  {day.getUTCDate()}
                </span>
              </div>
              {dayPosts.length === 0 ? (
                <button
                  onClick={() => onSchedule(day)}
                  className="flex-1 w-full min-h-[64px] flex flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-slate-300 px-1 text-[11px] font-medium leading-tight text-center text-slate-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition"
                >
                  <Plus size={15} />
                  <span>Programar publicación</span>
                </button>
              ) : (
                <div className="relative flex-1 space-y-1">
                  {dayPosts.slice(0, 3).map((p) => (
                    <PostChip key={p.id} post={p} onSelect={onSelect} />
                  ))}
                  {dayPosts.length > 3 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setExpandedKey(key); }}
                      className="w-full text-[11px] font-semibold text-slate-600 hover:text-brand-700 hover:bg-brand-50 rounded-md px-1.5 py-0.5 transition text-left"
                    >
                      +{dayPosts.length - 3} más
                    </button>
                  )}
                  {expandedKey === key && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setExpandedKey(null)}
                      />
                      {/* Popover */}
                      <div className="absolute left-0 right-0 top-0 z-50 bg-white rounded-lg border border-slate-200 shadow-xl p-2 max-h-[260px] overflow-y-auto">
                        <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-slate-100">
                          <span className="text-[11px] font-semibold text-slate-700">
                            {day.getUTCDate()} · {dayPosts.length} publicaciones
                          </span>
                          <button
                            type="button"
                            onClick={() => setExpandedKey(null)}
                            className="text-slate-400 hover:text-slate-700 transition"
                            aria-label="Cerrar"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="space-y-1">
                          {dayPosts.map((p) => (
                            <PostChip key={p.id} post={p} onSelect={(post) => { setExpandedKey(null); onSelect(post); }} />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Week view ──────────────────────────────────────────── */
function WeekView({
  cursor, today, posts, dragKey, setDragKey, onDropSlot, onSchedule, onSelect,
}: {
  cursor: Date;
  today: Date;
  posts: ZonedPost[];
  dragKey: string | null;
  setDragKey: Dispatch<SetStateAction<string | null>>;
  onDropSlot: (postId: string, day: Date, hour: number) => void;
  onSchedule: (day: Date, hour: number) => void;
  onSelect: (post: ZonedPost) => void;
}) {
  const weekStart = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* day header */}
      <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))' }}>
        <div />
        {days.map((day) => {
          const isToday = sameDay(day, today);
          return (
            <div key={day.toISOString()} className="px-2 py-2 text-center border-l border-slate-100">
              <div className="text-[11px] font-medium text-slate-400 uppercase">
                {WEEKDAYS[(day.getUTCDay() + 6) % 7]}
              </div>
              <div className={cn(
                'mt-0.5 inline-flex items-center justify-center h-6 w-6 text-xs font-semibold rounded-full',
                isToday ? 'bg-brand-600 text-white' : 'text-slate-700',
              )}>
                {day.getUTCDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* hour grid */}
      <div className="grid" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))' }}>
        {hours.map((hour) => (
          <HourRow
            key={hour}
            hour={hour}
            days={days}
            posts={posts}
            dragKey={dragKey}
            setDragKey={setDragKey}
            onDropSlot={onDropSlot}
            onSchedule={onSchedule}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function HourRow({
  hour, days, posts, dragKey, setDragKey, onDropSlot, onSchedule, onSelect,
}: {
  hour: number;
  days: Date[];
  posts: ZonedPost[];
  dragKey: string | null;
  setDragKey: Dispatch<SetStateAction<string | null>>;
  onDropSlot: (postId: string, day: Date, hour: number) => void;
  onSchedule: (day: Date, hour: number) => void;
  onSelect: (post: ZonedPost) => void;
}) {
  return (
    <>
      {/* hour gutter */}
      <div className="pr-2 pt-1 text-right text-[11px] text-slate-400 tabular-nums border-b border-slate-100"
        style={{ minHeight: HOUR_ROW }}>
        {String(hour).padStart(2, '0')}:00
      </div>
      {/* day cells */}
      {days.map((day) => {
        const key = `${day.toISOString()}|${hour}`;
        const slotPosts = posts
          .filter((p) => sameDay(p.zoned, day) && p.zoned.getUTCHours() === hour)
          .sort((a, b) => a.zoned.getTime() - b.zoned.getTime());

        return (
          <div
            key={key}
            onDragOver={(e) => { e.preventDefault(); setDragKey(key); }}
            onDragLeave={() => setDragKey((k) => (k === key ? null : k))}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData('text/plain');
              setDragKey(null);
              if (id) onDropSlot(id, day, hour);
            }}
            className={cn(
              'group border-b border-l border-slate-100 p-1 space-y-1 transition-colors min-w-0 overflow-hidden',
              dragKey === key && 'bg-brand-50 ring-1 ring-inset ring-brand-300',
            )}
            style={{ minHeight: HOUR_ROW }}
          >
            {slotPosts.length === 0 ? (
              <button
                onClick={() => onSchedule(day, hour)}
                title={`Programar a las ${String(hour).padStart(2, '0')}:00`}
                className="w-full flex items-center justify-center rounded-md text-slate-300 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-brand-600 hover:bg-brand-50 transition"
                style={{ minHeight: HOUR_ROW - 8 }}
              >
                <Plus size={15} />
              </button>
            ) : (
              slotPosts.map((p) => (
                <PostChip key={p.id} post={p} onSelect={onSelect} />
              ))
            )}
          </div>
        );
      })}
    </>
  );
}

/* ── Tiny account avatar for calendar chips ─────────────── */
/**
 * Slightly tweaked version of AccountAvatar tuned for the tight calendar
 * chip: 22 px circle with an oversized 11 px platform badge so both the
 * profile photo and the network icon remain clearly visible at a glance.
 */
function ChipAccountAvatar({
  target,
}: {
  target: ZonedPost['targets'][number];
}) {
  const [imgError, setImgError] = useState(false);
  const meta = PLATFORM_META[target.platform as Platform];
  const Icon = meta?.icon;
  const hasPhoto = Boolean(target.profilePhotoUrl) && !imgError;
  const label = target.displayName ?? target.username ?? meta?.label ?? target.platform;

  const SIZE = 22;
  const BADGE = 12;

  return (
    <span
      className="relative inline-flex shrink-0 ring-2 ring-white rounded-full bg-white"
      style={{ width: SIZE, height: SIZE }}
      title={label}
    >
      {hasPhoto ? (
        <img
          src={target.profilePhotoUrl!}
          alt={label}
          className="rounded-full object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      ) : (
        meta && Icon && (
          <span
            className="inline-flex w-full h-full items-center justify-center rounded-full"
            style={{ backgroundColor: meta.color }}
          >
            <Icon size={Math.round(SIZE * 0.55)} color="white" />
          </span>
        )
      )}
      {/* Platform badge — only when showing the actual photo (otherwise the
          whole circle already is the platform icon). */}
      {hasPhoto && meta && Icon && (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full ring-[1.5px] ring-white"
          style={{ width: BADGE, height: BADGE, backgroundColor: meta.color }}
        >
          <Icon size={Math.round(BADGE * 0.65)} color="white" />
        </span>
      )}
    </span>
  );
}

/* ── Post chip ──────────────────────────────────────────── */
function PostChip({ post, onSelect }: { post: ZonedPost; onSelect: (post: ZonedPost) => void }) {
  const st = STATUS_STYLES[post.status] ?? STATUS_STYLES.draft;
  const canDrag = post.status === 'scheduled' || post.status === 'draft';
  const time = fmtTime(post.zoned);
  const [tip, setTip] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  function handleClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('[data-no-open]')) return;
    onSelect(post);
  }

  function showTip(e: React.MouseEvent) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 308));
    // Flip above the chip when there isn't room below.
    const below = r.bottom + 280 < window.innerHeight;
    setTip(below ? { left, top: r.bottom + 6 } : { left, bottom: window.innerHeight - r.top + 6 });
  }

  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', post.id);
        e.dataTransfer.effectAllowed = 'move';
        setTip(null);
      }}
      onClick={handleClick}
      onMouseEnter={showTip}
      onMouseLeave={() => setTip(null)}
      className={cn(
        'rounded-md border px-1.5 py-1 text-[11px] leading-tight transition-colors select-none',
        st.chip,
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
      )}
    >
      <div className="flex items-center gap-1.5">
        {/* Stacked account avatars (profile photo + platform badge) */}
        <div className="flex -space-x-1.5 shrink-0">
          {post.targets.slice(0, 3).map((t) => (
            <ChipAccountAvatar key={t.socialAccountId} target={t} />
          ))}
        </div>
        {post.targets.length > 3 && (
          <span className="text-[9px] font-semibold opacity-70">+{post.targets.length - 3}</span>
        )}
        <span className="font-semibold tabular-nums ml-auto">{time}</span>
      </div>
      {post.caption && (
        <div className="truncate mt-0.5 opacity-80">{post.caption}</div>
      )}

      {tip && createPortal(
        <div
          style={{ position: 'fixed', left: tip.left, top: tip.top, bottom: tip.bottom }}
          className="z-[60] w-[300px] rounded-xl border border-slate-200 bg-white shadow-xl p-3 pointer-events-none"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full', st.chip)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} /> {st.label}
            </span>
            <span className="text-[11px] text-slate-400 ml-auto tabular-nums">{time}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {post.targets.map((t) => {
              const meta = PLATFORM_META[t.platform as Platform];
              const Icon = meta?.icon;
              return (
                <span key={t.socialAccountId} className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full pl-0.5 pr-2 py-0.5">
                  <ChipAccountAvatar target={t} />
                  <span className="truncate max-w-[110px]">{t.username ?? t.displayName ?? meta?.label}</span>
                  {Icon && <Icon size={10} color={meta.color} />}
                </span>
              );
            })}
          </div>
          <p className={cn('text-xs leading-snug whitespace-pre-wrap line-clamp-6', post.caption ? 'text-slate-700' : 'text-slate-400 italic')}>
            {post.caption || 'Sin texto'}
          </p>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ── Legend ─────────────────────────────────────────────── */
function Legend({ hasPosts }: { hasPosts: boolean }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex flex-wrap gap-x-10 gap-y-5">
        {/* status legend */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">Estado</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {(['published', 'scheduled', 'publishing', 'failed'] as PostStatus[]).map((s) => {
              const st = STATUS_STYLES[s];
              return (
                <div key={s} className="flex items-center gap-2">
                  <span className={cn('h-3 w-3 rounded-full', st.dot)} />
                  <span className="text-sm text-slate-600">{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* platform legend */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">Red social</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {LEGEND_PLATFORMS.map((p) => {
              const meta = PLATFORM_META[p];
              const Icon = meta.icon;
              return (
                <div key={p} className="flex items-center gap-1.5">
                  <Icon size={14} color={meta.color} />
                  <span className="text-sm text-slate-600">{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!hasPosts && (
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-400">
          <CalendarDays size={15} />
          Aún no tienes publicaciones programadas. Créalas desde «Crear publicación».
        </div>
      )}
    </div>
  );
}

/* ── Post preview modal ─────────────────────────────────── */
function PostPreviewModal({
  post,
  tz,
  onClose,
}: {
  post: ZonedPost;
  tz: string;
  onClose: () => void;
}) {
  const st = STATUS_STYLES[post.status] ?? STATUS_STYLES.draft;
  const when = post.publishedAt ?? post.scheduledAt;
  const liveTargets = post.targets.filter((t) => t.platformUrl);

  // Close on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-slate-100">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-2.5 py-1 shrink-0',
              st.chip.split(' ').filter((c) => !c.startsWith('hover:')).join(' '),
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} />
            {st.label}
          </span>
          <div className="flex items-center gap-1.5">
            {/* Only scheduled / draft posts are still editable — anything in
                the publish pipeline or already done is read-only. */}
            {(post.status === 'scheduled' || post.status === 'draft') && (
              <Link
                to={`/dashboard/posts/${post.id}/edit`}
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md px-2.5 py-1.5 transition"
                title="Editar publicación"
              >
                <Pencil size={12} />
                Editar publicación
              </Link>
            )}
            {(post.status === 'published' || post.status === 'partial_failed') && (
              <ReusePostButton
                media={post.media}
                caption={post.caption}
                usedAccountIds={post.targets.map((t) => t.socialAccountId)}
                variant="modal"
                onBeforeNavigate={onClose}
              />
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* media */}
        {post.media[0] && (
          <div className="bg-slate-50 flex items-center justify-center border-b border-slate-100">
            {post.media[0].type === 'video' ? (
              <div className="relative w-full">
                <video
                  src={post.media[0].url}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full max-h-80 object-contain bg-black"
                />
              </div>
            ) : (
              <img
                src={post.media[0].url}
                alt=""
                className="w-full max-h-80 object-contain"
              />
            )}
          </div>
        )}

        {/* body */}
        <div className="px-5 py-4 space-y-4">
          {/* accounts */}
          {post.targets.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                {post.targets.length === 1 ? 'Cuenta' : 'Cuentas'}
              </p>
              <div className="space-y-1.5">
                {post.targets.map((t) => {
                  const meta = PLATFORM_META[t.platform as Platform];
                  return (
                    <div key={t.socialAccountId} className="flex items-center gap-2.5">
                      <AccountAvatar
                        account={{
                          platform: t.platform as Platform,
                          username: t.username,
                          displayName: t.displayName,
                          profilePhotoUrl: t.profilePhotoUrl,
                        }}
                        size={28}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {t.displayName || t.username || meta?.label || t.platform}
                        </p>
                        {t.username && t.displayName && (
                          <p className="text-xs text-slate-500 truncate">@{t.username}</p>
                        )}
                      </div>
                      {t.platformUrl && (
                        <a
                          href={t.platformUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Abrir en ${meta?.label ?? t.platform}`}
                          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md px-2 py-1 transition"
                        >
                          Ver publicación <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* caption */}
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
              Texto
            </p>
            <p className={cn(
              'text-sm whitespace-pre-line leading-relaxed',
              post.caption ? 'text-slate-800' : 'text-slate-400 italic',
            )}>
              {post.caption || 'Sin texto'}
            </p>
          </div>

          {/* time */}
          {when && (
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                {post.status === 'published' ? 'Publicado' : 'Programado'}
              </p>
              <p className="text-sm text-slate-700">
                {new Date(when).toLocaleString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hourCycle: 'h23',
                  timeZone: tz,
                })}
              </p>
            </div>
          )}
        </div>

        {/* footer */}
        {liveTargets.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <a
              href={liveTargets[0]!.platformUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center gap-2"
            >
              <ExternalLink size={15} />
              Ver publicación en {PLATFORM_META[liveTargets[0]!.platform as Platform]?.label ?? 'la red social'}
            </a>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
